import React, { createContext, useContext, useReducer, useEffect, useRef, useMemo, useCallback } from 'react';
import moment from 'moment';
import { useLocation, useSearchParams } from 'react-router-dom';
import { createStore } from '../services/storage/storageService';
import { sessionAdapter } from '../services/storage/adapters';
import {
  applyDateFiltersToSearchParams,
  parseDateFiltersFromSearchParams,
} from '../hooks/useDateFilterUrlParams';

export const FILTER_SCOPES = {
  stores: 'stores',
  storeAuditReport: 'storeAuditReport',
} as const;

export type FilterScope = typeof FILTER_SCOPES[keyof typeof FILTER_SCOPES];

// The stores dashboard defaults to the last completed day; audit pages retain seven days.
const getDefaultDateRange = (scope?: FilterScope) => {
  const today = new Date();
  if (scope === FILTER_SCOPES.stores) {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return { startDate: yesterday, endDate: yesterday };
  }

  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 6);
  return { startDate, endDate: today };
};

// Helper function to format date with timezone
const formatDateWithTimezone = (date: any, isStartDate: boolean) => {
  // Ensure we're working with a proper date object
  const dateObj = moment.isMoment(date) ? date : moment(date);
  
  // Use local timezone to prevent date shifting
  if (isStartDate) {
    // For start date, use 00:00:00 in local timezone
    return dateObj.format('YYYY-MM-DD') + 'T00:00:00.000';
  } else {
    // For end date, use 23:59:59 in local timezone
    return dateObj.format('YYYY-MM-DD') + 'T23:59:59.000';
  }
};

// Helper function to calculate days between dates
const calculateDays = (startDate: any, endDate: any) => {
  const start = moment(startDate);
  const end = moment(endDate);
  return end.diff(start, 'days') + 1;
};

// Default filter state
const defaultFilters = {
  startDate: null,
  endDate: null,
  count: null,
};

// Initialize a namespaced storage for date filters (session-scoped)
const dateFiltersStore = createStore({
  namespace: 'dateFilters',
  adapter: sessionAdapter,
  validate: (value: any) => {
    if (!value || typeof value !== 'object') return false;
    const { startDate, endDate, count } = value;
    if (typeof startDate !== 'string' || typeof endDate !== 'string') return false;
    if (count != null && typeof count !== 'string') return false;
    return true;
  },
});

// Initialize filters with stored values or default date range
const initializeFilters = (scope: FilterScope) => {
  const urlParams = new URLSearchParams(window.location.search);
  const parsedURLFilters = parseDateFiltersFromSearchParams(urlParams);

  if (parsedURLFilters) {
    return parsedURLFilters;
  }

  // A bare Stores URL always uses its dashboard default. Other scopes retain
  // their session range when no explicit URL range is present.
  if (scope !== FILTER_SCOPES.stores) {
    const stored = dateFiltersStore.get(scope);
    if (stored && stored.startDate && stored.endDate) {
      const storedStart = moment(stored.startDate);
      const storedEnd = moment(stored.endDate);
      if (storedStart.isValid() && storedEnd.isValid()) {
        return {
          startDate: stored.startDate,
          endDate: stored.endDate,
          count: stored.count ?? calculateDays(stored.startDate, stored.endDate).toString(),
        };
      }
      dateFiltersStore.remove(scope);
    }
  }
  
  const defaultRange = getDefaultDateRange(scope);
  return {
    startDate: formatDateWithTimezone(defaultRange.startDate, true),
    endDate: formatDateWithTimezone(defaultRange.endDate, false),
    count: calculateDays(defaultRange.startDate, defaultRange.endDate).toString(),
  };
};

// Action types
const FILTER_ACTIONS = {
  SET_DATE_RANGE: 'SET_DATE_RANGE',
  RESET_DATE_RANGE: 'RESET_DATE_RANGE',
  CLEAR_ALL_FILTERS: 'CLEAR_ALL_FILTERS',
  SYNC_FROM_URL: 'SYNC_FROM_URL',
  OVERRIDE_URL: 'OVERRIDE_URL',
};

// Filter state interface
interface FilterState {
  startDate: string | null;
  endDate: string | null;
  count: string | null;
}

// Action interface
interface FilterAction {
  type: string;
  payload?: any;
}

const serializeFilters = (filters: Pick<FilterState, 'startDate' | 'endDate' | 'count'>) =>
  JSON.stringify({
    startDate: filters.startDate,
    endDate: filters.endDate,
    count: filters.count,
  });

const areFiltersEqual = (
  left: Pick<FilterState, 'startDate' | 'endDate' | 'count'>,
  right: Pick<FilterState, 'startDate' | 'endDate' | 'count'>
) =>
  left.startDate === right.startDate &&
  left.endDate === right.endDate &&
  left.count === right.count;

// Filter reducer
const filterReducer = (state: FilterState, action: FilterAction): FilterState => {
  switch (action.type) {
    case FILTER_ACTIONS.SET_DATE_RANGE:
      const { startDate, endDate } = action.payload;
      const startDateFormatted = formatDateWithTimezone(startDate, true);
      const endDateFormatted = formatDateWithTimezone(endDate, false);
      const count = calculateDays(startDate, endDate);
      
      console.log('FilterContext - SET_DATE_RANGE:', {
        originalStartDate: startDate,
        originalEndDate: endDate,
        formattedStartDate: startDateFormatted,
        formattedEndDate: endDateFormatted,
        count
      });
      
      return {
        ...state,
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        count: count.toString(),
      };
    
    case FILTER_ACTIONS.RESET_DATE_RANGE:
      const defaultRange = getDefaultDateRange(action.payload?.scope);
      return setDateRange(state, defaultRange.startDate, defaultRange.endDate);
    
    case FILTER_ACTIONS.CLEAR_ALL_FILTERS:
      const clearDefaultRange = getDefaultDateRange(action.payload?.scope);
      return {
        ...defaultFilters,
        startDate: formatDateWithTimezone(clearDefaultRange.startDate, true),
        endDate: formatDateWithTimezone(clearDefaultRange.endDate, false),
        count: calculateDays(clearDefaultRange.startDate, clearDefaultRange.endDate).toString(),
      };
    
    case FILTER_ACTIONS.SYNC_FROM_URL:
      const { startDate: urlStartDate, endDate: urlEndDate, count: urlCount } = action.payload;
      if (urlStartDate && urlEndDate) {
        const startDateFormatted = formatDateWithTimezone(urlStartDate, true);
        const endDateFormatted = formatDateWithTimezone(urlEndDate, false);
        const calculatedCount = urlCount || calculateDays(urlStartDate, urlEndDate).toString();
        
        console.log('FilterContext - SYNC_FROM_URL:', {
          urlStartDate,
          urlEndDate,
          urlCount,
          formattedStartDate: startDateFormatted,
          formattedEndDate: endDateFormatted,
          calculatedCount
        });
        
        return {
          ...state,
          startDate: startDateFormatted,
          endDate: endDateFormatted,
          count: calculatedCount,
        };
      }
      return state;
    
    case FILTER_ACTIONS.OVERRIDE_URL:
      return state;
    
    default:
      return state;
  }
};

// Set date range helper function
const setDateRange = (filters: FilterState, startDate: any, endDate: any): FilterState => {
  const startDateFormatted = formatDateWithTimezone(startDate, true);
  const endDateFormatted = formatDateWithTimezone(endDate, false);
  const count = calculateDays(startDate, endDate);
  
  return {
    ...filters,
    startDate: startDateFormatted,
    endDate: endDateFormatted,
    count: count.toString(),
  };
};

// Reset date range to default
const resetDateRange = (filters: FilterState): FilterState => {
  const defaultRange = getDefaultDateRange();
  return setDateRange(filters, defaultRange.startDate, defaultRange.endDate);
};

// Clear all filters
const clearAllFilters = (): FilterState => {
  const defaultRange = getDefaultDateRange();
  return {
    ...defaultFilters,
    startDate: formatDateWithTimezone(defaultRange.startDate, true),
    endDate: formatDateWithTimezone(defaultRange.endDate, false),
    count: calculateDays(defaultRange.startDate, defaultRange.endDate).toString(),
  };
};

// Get filters for API
const getFiltersForAPI = (filters: FilterState) => {
  const apiFilters: any = {};
  
  if (filters.startDate) apiFilters.start_date = moment(filters.startDate).format('YYYY-MM-DD');
  if (filters.endDate) apiFilters.end_date = moment(filters.endDate).format('YYYY-MM-DD');
  if (filters.count) apiFilters.count = filters.count;
  
  return apiFilters;
};

// Context interface
interface FilterContextType {
  filters: FilterState;
  setDateRange: (startDate: any, endDate: any) => void;
  resetDateRange: () => void;
  clearAllFilters: () => void;
  getFiltersForAPI: () => any;
}

// Create context with proper default value
const FilterContext = createContext<FilterContextType | undefined>(undefined);

// Filter Provider Component
export const FilterProvider = ({
  children,
  scope,
}: {
  children: React.ReactNode;
  scope: FilterScope;
}) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, dispatch] = useReducer(filterReducer, scope, initializeFilters);
  const isInitialized = useRef(false);
  const filtersRef = useRef(filters);
  const skipNextUrlWrite = useRef(false);
  const nextUrlWriteShouldReplace = useRef(true);

  // Persist filters to storage whenever they change
  useEffect(() => {
    if (isInitialized.current && filters.startDate && filters.endDate) {
      console.log('FilterContext - Persisting filters to storage:', filters);
      dateFiltersStore.set(scope, {
        startDate: filters.startDate,
        endDate: filters.endDate,
        count: filters.count,
      });
    }
  }, [filters.startDate, filters.endDate, filters.count, scope]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  // Initialize on mount
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      if (filters.startDate && filters.endDate) {
        dateFiltersStore.set(scope, {
          startDate: filters.startDate,
          endDate: filters.endDate,
          count: filters.count,
        });
      }
    }
  }, [filters.startDate, filters.endDate, filters.count, scope]);

  useEffect(() => {
    if (!isInitialized.current) {
      return;
    }

    const parsedURLFilters = parseDateFiltersFromSearchParams(searchParams);
    if (!parsedURLFilters) {
      return;
    }

    if (!areFiltersEqual(filtersRef.current, parsedURLFilters)) {
      skipNextUrlWrite.current = true;
      dispatch({
        type: FILTER_ACTIONS.SYNC_FROM_URL,
        payload: parsedURLFilters,
      });
    }
  }, [location.search, searchParams]);

  useEffect(() => {
    if (!isInitialized.current || !filters.startDate || !filters.endDate) {
      return;
    }

    if (skipNextUrlWrite.current) {
      skipNextUrlWrite.current = false;
      return;
    }

    const nextParams = applyDateFiltersToSearchParams(searchParams, filters);

    if (nextParams.toString() !== searchParams.toString()) {
      const shouldReplace = nextUrlWriteShouldReplace.current;
      nextUrlWriteShouldReplace.current = true;
      setSearchParams(nextParams, { replace: shouldReplace });
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.count,
    searchParams,
    setSearchParams,
  ]);

  // Action creators
  const setDateRangeAction = useCallback((startDate: any, endDate: any) => {
    console.log('FilterContext - setDateRangeAction called:', { startDate, endDate });
    nextUrlWriteShouldReplace.current = false;
    dispatch({
      type: FILTER_ACTIONS.SET_DATE_RANGE,
      payload: { startDate, endDate },
    });
  }, []);

  const resetDateRangeAction = useCallback(() => {
    console.log('FilterContext - resetDateRangeAction called');
    nextUrlWriteShouldReplace.current = false;
    dispatch({
      type: FILTER_ACTIONS.RESET_DATE_RANGE,
      payload: { scope },
    });
    // Clear stored values when resetting to default
    dateFiltersStore.remove(scope);
  }, [scope]);

  const clearAllFiltersAction = useCallback(() => {
    console.log('FilterContext - clearAllFiltersAction called');
    nextUrlWriteShouldReplace.current = false;
    dispatch({
      type: FILTER_ACTIONS.CLEAR_ALL_FILTERS,
      payload: { scope },
    });
    dateFiltersStore.remove(scope);
  }, [scope]);

  const contextValue = useMemo<FilterContextType>(() => ({
    filters,
    setDateRange: setDateRangeAction,
    resetDateRange: resetDateRangeAction,
    clearAllFilters: clearAllFiltersAction,
    getFiltersForAPI: () => getFiltersForAPI(filters),
  }), [serializeFilters(filters), setDateRangeAction, resetDateRangeAction, clearAllFiltersAction]);

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
};

// Custom hook to use filter context
export const useFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useFilterContext must be used within a FilterProvider');
  }
  return context;
};

export const useOptionalFilterContext = () => useContext(FilterContext);

// Export utility functions for backward compatibility
export {
  getDefaultDateRange,
  formatDateWithTimezone,
  calculateDays,
  defaultFilters,
  initializeFilters,
  setDateRange,
  resetDateRange,
  clearAllFilters,
  getFiltersForAPI,
}; 
